import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Inbox, PackagePlus, X } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RequestStockDialog } from './RequestStockDialog';

const STATUS = { open: 'Waiting', fulfilled: 'Sent', declined: 'Declined', cancelled: 'Cancelled' };

const Lines = ({ items, isHead }) => (
  <ul className="mt-2 grid gap-y-1 gap-x-8 text-xs text-muted-foreground sm:grid-cols-2">
    {items.map(i => (
      <li key={i.product_id} className="flex justify-between gap-3 border-b border-dashed py-1">
        <span className="truncate text-foreground">{i.product_name}</span>
        <span className={isHead && i.head_available < i.quantity ? 'text-red-700' : ''}>
          {i.quantity} asked{isHead && i.head_available !== undefined ? ` · ${i.head_available} at head` : ''}
        </span>
      </li>
    ))}
  </ul>
);

export const StockRequestsPanel = ({ branchKey, onFulfil, refreshKey }) => {
  const [data, setData] = useState(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get('/stock-requests').then(r => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load, branchKey, refreshKey]);

  if (!data) return null;
  const { requests, is_head: isHead } = data;
  const shown = isHead ? requests.filter(r => r.status === 'open') : requests.slice(0, 6);

  const act = async (r, action) => {
    setBusy(r.request_id);
    try {
      await api.post(`/stock-requests/${r.request_id}/${action}`, action === 'decline' ? { reason: '' } : undefined);
      toast.success(action === 'decline' ? 'Request declined' : 'Request cancelled');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update the request'); }
    finally { setBusy(null); }
  };

  return (
    <section className="space-y-3 border rounded-md p-4" data-testid="stock-requests-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><Inbox className="h-4 w-4 text-primary" />Stock-up requests</h2>
          <p className="text-xs text-muted-foreground">{isHead ? 'Branches asking the head location for stock' : 'Ask your head location to send stock'}</p>
        </div>
        {!isHead && <Button size="sm" onClick={() => setAsking(true)} data-testid="stock-request-new"><PackagePlus className="h-4 w-4 mr-1" />Request stock</Button>}
      </div>
      {!shown.length ? <p className="text-sm text-muted-foreground" data-testid="stock-requests-empty">{isHead ? 'No open requests.' : 'No requests yet.'}</p> : (
        <div className="divide-y">
          {shown.map(r => (
            <div key={r.request_id} className="py-3" data-testid={`stock-request-${r.request_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-sm">{isHead ? r.branch_name : r.request_id}</span>
                <Badge variant={r.status === 'open' ? 'default' : 'secondary'} data-testid={`stock-request-status-${r.request_id}`}>{STATUS[r.status]}</Badge>
                <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
                <span className="flex-1" />
                {isHead && r.status === 'open' && <>
                  <Button size="sm" onClick={() => onFulfil(r)} disabled={Boolean(busy)} data-testid={`stock-request-fulfil-${r.request_id}`}>Turn into transfer</Button>
                  <Button size="sm" variant="ghost" onClick={() => act(r, 'decline')} disabled={Boolean(busy)} data-testid={`stock-request-decline-${r.request_id}`}>Decline</Button>
                </>}
                {!isHead && r.status === 'open' && <Button size="sm" variant="ghost" onClick={() => act(r, 'cancel')} disabled={Boolean(busy)} data-testid={`stock-request-cancel-${r.request_id}`}><X className="h-4 w-4 mr-1" />Cancel</Button>}
              </div>
              {r.note && <p className="mt-1 text-xs italic text-muted-foreground">“{r.note}”</p>}
              <Lines items={r.items} isHead={isHead} />
            </div>
          ))}
        </div>
      )}
      {asking && <RequestStockDialog onClose={() => setAsking(false)} onSent={load} />}
    </section>
  );
};
